import React from 'react';
import { Package } from 'lucide-react';

export type FlowStockBadgeSeverity = 'normal' | 'warning' | 'critical';

interface FlowStockBadgeProps {
  value: number;
  severity?: FlowStockBadgeSeverity;
  x: number;
  y: number;
}

interface StockBadgeStyle {
  textColor: string;
  iconColor: string;
  shadowColor: string;
}

export function getStockBadgeStyle(
  value: number,
  severity: FlowStockBadgeSeverity = 'normal'
): StockBadgeStyle {
  if (value <= 0) {
    return {
      textColor: '#475569',
      iconColor: '#64748B',
      shadowColor: 'rgba(71, 85, 105, 0.18)',
    };
  }

  if (severity === 'warning') {
    return {
      textColor: '#C2410C',
      iconColor: '#C2410C',
      shadowColor: 'rgba(249, 115, 22, 0.22)',
    };
  }

  if (severity === 'critical') {
    return {
      textColor: '#991B1B',
      iconColor: '#991B1B',
      shadowColor: 'rgba(220, 38, 38, 0.22)',
    };
  }

  return {
    textColor: '#1E293B',
    iconColor: '#475569',
    shadowColor: 'rgba(30, 41, 59, 0.16)',
  };
}

export const FlowStockBadge: React.FC<FlowStockBadgeProps> = ({
  value,
  severity = 'normal',
  x,
  y,
}) => {
  const safeValue = Number.isFinite(value) ? Math.max(0, Math.round(value)) : 0;
  const style = getStockBadgeStyle(safeValue, severity);
  const valueLabel = `+${safeValue}`;

  const iconSize = severity === 'critical' ? 17 : 16;
  const textWidth = Math.max(24, valueLabel.length * 10);
  const totalWidth = iconSize + 6 + textWidth;
  const anchorX = Math.round(x);
  const anchorY = Math.round(y);
  const startX = Math.round(-totalWidth / 2);

  return (
    <g
      transform={`translate(${anchorX}, ${anchorY})`}
      style={{ pointerEvents: 'none' }}
      shapeRendering="geometricPrecision"
      textRendering="geometricPrecision"
    >
      <g transform={`translate(${startX}, 0)`}>
        <g transform={`translate(0, ${-iconSize / 2})`}>
          <Package
            size={iconSize}
            color={style.iconColor}
            strokeWidth={2.2}
            style={{ vectorEffect: 'non-scaling-stroke' }}
          />
        </g>

        <text
          x={iconSize + 6}
          y={0}
          fill={style.textColor}
          fontSize={17}
          fontWeight={900}
          fontFamily="'Segoe UI', Arial, sans-serif"
          letterSpacing="-0.2"
          dominantBaseline="middle"
        >
          {valueLabel}
        </text>
      </g>
    </g>
  );
};

export default FlowStockBadge;
